import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './EventDetails.css';
import { weddingConfig } from '../config/weddingConfig';

const EventDetails = () => {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const [selectedEventSlug, setSelectedEventSlug] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Collect all events in order from all days
  const allEvents = useMemo(() => {
    const events = [];
    weddingConfig.content.schedule.days.forEach(day => {
      day.events.forEach(event => {
        events.push({
          ...event,
          date: day.date
        });
      });
    });
    return events;
  }, []);

  // Check if mobile view (screen width < 768px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine initial selected event
  useEffect(() => {
    if (eventSlug) {
      const eventExists = allEvents.find(e => e.slug === eventSlug);
      if (eventExists) {
        setSelectedEventSlug(eventSlug);
        setShowDetails(true);
        // Scroll to event details container when event is loaded in mobile view
        if (isMobile) {
          setTimeout(() => {
            const container = document.querySelector('.event-details-mobile');
            if (container) {
              const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
            }
          }, 100);
        }
      } else {
        // If event not found, select first event and navigate to it
        if (allEvents.length > 0) {
          const firstSlug = allEvents[0].slug;
          setSelectedEventSlug(firstSlug);
          setShowDetails(true);
          navigate(`/event/${firstSlug}`, { replace: true });
          if (isMobile) {
            setTimeout(() => {
              const container = document.querySelector('.event-details-mobile');
              if (container) {
                const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
              }
            }, 100);
          }
        }
      }
    } else {
      // No event slug, select first event by default and navigate to it
      if (allEvents.length > 0) {
        const firstSlug = allEvents[0].slug;
        setSelectedEventSlug(firstSlug);
        setShowDetails(true);
        navigate(`/event/${firstSlug}`, { replace: true });
        if (isMobile) {
          setTimeout(() => {
            const container = document.querySelector('.event-details-mobile');
            if (container) {
              const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
            }
          }, 100);
        }
      }
    }
  }, [eventSlug, allEvents, navigate, isMobile]);

  // Find the selected event
  const selectedEvent = allEvents.find(e => e.slug === selectedEventSlug);

  // Scroll to event details container when showDetails becomes true in mobile view
  useEffect(() => {
    if (isMobile && showDetails && selectedEvent) {
      setTimeout(() => {
        const container = document.querySelector('.event-details-mobile');
        if (container) {
          const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isMobile, showDetails, selectedEvent]);

  const handleEventSelect = (slug) => {
    setSelectedEventSlug(slug);
    // Update URL for both mobile and desktop
    navigate(`/event/${slug}`, { replace: true });
    if (isMobile) {
      setShowDetails(true);
      // Scroll to event details container when event is selected in mobile view
      setTimeout(() => {
        const container = document.querySelector('.event-details-mobile');
        if (container) {
          const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleBackToMenu = () => {
    if (isMobile) {
      setShowDetails(false);
    }
  };

  // Mobile view: show menu or details
  if (isMobile) {
    if (!showDetails) {
      // Show menu
      return (
        <div className="event-details-container">
          <div className="event-menu-mobile">
            <h2 className="events-title" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
              Events
            </h2>
            <div className="event-menu-list">
              {allEvents.map((event, index) => (
                <div
                  key={index}
                  className={`event-menu-item ${selectedEventSlug === event.slug ? 'active' : ''}`}
                  onClick={() => handleEventSelect(event.slug)}
                  style={{
                    fontFamily: weddingConfig.design.fonts.body,
                    color: selectedEventSlug === event.slug ? weddingConfig.design.colors.primary : weddingConfig.design.colors.secondary,
                    borderColor: selectedEventSlug === event.slug ? weddingConfig.design.colors.primary : 'transparent'
                  }}
                >
                  {event.title}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      // Show details with back button
      if (!selectedEvent) {
        return (
          <div className="event-details-container">
            <div className="container">
              <h2 style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
                Event Not Found
              </h2>
              <button 
                onClick={() => navigate('/schedule')}
                style={{ 
                  fontFamily: weddingConfig.design.fonts.body,
                  marginTop: '20px',
                  padding: '10px 20px',
                  backgroundColor: weddingConfig.design.colors.primary,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Back to Schedule
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="event-details-container">
          <div className="event-details-mobile">
            <button 
              onClick={handleBackToMenu}
              className="back-button"
              style={{ 
                fontFamily: weddingConfig.design.fonts.body,
                color: weddingConfig.design.colors.primary,
                borderColor: weddingConfig.design.colors.primary
              }}
            >
              ← Back to Events
            </button>
            <EventContent event={selectedEvent} />
          </div>
        </div>
      );
    }
  }

  // Desktop view: show sidebar and details side by side
  if (!selectedEvent) {
    return (
      <div className="event-details-container">
        <div className="container">
          <h2 style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
            Event Not Found
          </h2>
          <button 
            onClick={() => navigate('/schedule')}
            style={{ 
              fontFamily: weddingConfig.design.fonts.body,
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: weddingConfig.design.colors.primary,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Schedule
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-details-container">
      <div className="event-details-layout">
        {/* Left Sidebar - Event Menu */}
        <div className="event-menu-sidebar">
          <h2 className="events-title" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
            Events
          </h2>
          <div className="event-menu-list">
            {allEvents.map((event, index) => (
              <div
                key={index}
                className={`event-menu-item ${selectedEventSlug === event.slug ? 'active' : ''}`}
                onClick={() => handleEventSelect(event.slug)}
                style={{
                  fontFamily: weddingConfig.design.fonts.body,
                  color: selectedEventSlug === event.slug ? weddingConfig.design.colors.primary : weddingConfig.design.colors.secondary,
                  borderColor: selectedEventSlug === event.slug ? weddingConfig.design.colors.primary : 'transparent',
                  backgroundColor: selectedEventSlug === event.slug ? 'rgba(58, 37, 18, 0.05)' : 'transparent'
                }}
              >
                {event.title}
              </div>
            ))}
          </div>
        </div>

        {/* Right Content - Event Details */}
        <div className="event-details-content-wrapper">
          <EventContent event={selectedEvent} />
        </div>
      </div>
    </div>
  );
};

// Event Content Component
const EventContent = ({ event }) => {
  if (!event) return null;

  return (
    <div className="event-details-content">
      <h1 className="event-details-title" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary, marginBottom: event.nickname ? '2px' : '20px' }}>
        {event.title}
      </h1>
      {event.nickname && (
        <div className="event-details-nickname" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary, marginTop: '0px', marginBottom: '35px' }}>
          {event.nickname}
        </div>
      )}
      <div className="event-details-date" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary }}>
        {event.date} • {event.time}
      </div>

      <div className="event-details-section">
        <h2 className="section-heading" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
          About {event.title}
        </h2>
        <div className="section-content" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary }}>
          {event.definition.split('\n').map((line, index) => (
            <p key={index} style={{ marginBottom: line.trim() ? '1rem' : '0.5rem', whiteSpace: 'pre-wrap' }}>
              {line.trim() || '\u00A0'}
            </p>
          ))}
        </div>
      </div>

      {event.expectations && (
        <div className="event-details-section">
          <h2 className="section-heading" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
            What to Expect
          </h2>
          <div className="section-content" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary }}>
            {event.expectations.split('\n').map((line, index) => (
              <p key={index} style={{ marginBottom: line.trim() ? '0.8rem' : '0.3rem', whiteSpace: 'pre-wrap' }}>
                {line.trim() || '\u00A0'}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="event-details-section">
        <h2 className="section-heading" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
          Dress Code
        </h2>
        <div className="section-content" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary }}>
          {event.dressCode.split('\n').map((line, index) => (
            <p key={index} style={{ marginBottom: line.trim() ? '0.8rem' : '0.3rem', whiteSpace: 'pre-wrap' }}>
              {line.trim() || '\u00A0'}
            </p>
          ))}
        </div>
      </div>

      <div className="event-details-section">
        <h2 className="section-heading" style={{ fontFamily: weddingConfig.design.fonts.heading, color: weddingConfig.design.colors.primary }}>
          Location
        </h2>
        <p className="section-content" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary }}>
          <strong>{weddingConfig.resort.name}</strong>
        </p>
        <p className="section-content" style={{ fontFamily: weddingConfig.design.fonts.body, color: weddingConfig.design.colors.secondary }}>
          {weddingConfig.resort.address}
        </p>
      </div>
    </div>
  );
};

export default EventDetails;

